import { useState } from "react";

import {
  FieldArray,
  NoDataTile,
  Stack,
  SubmissionForm,
  useTheme
} from "@bps/fluent-ui";
import { SoftwarePackageVersionConfig } from "@libs/api/gateways/field/field-ops-gateway.dtos";
import { useSoftwarePackageVersionConfig } from "@libs/api/gateways/field/field-ops-gateway.hooks";

import {
  RolloutPackageDesiredConfigFormValues,
  RolloutPackageFormValues
} from "../../rollout/rolloutFormValues";
import { RolloutPackageFilterValues } from "../RolloutPackageFilter";
import { RolloutPackageConfigureDto } from "./";
import { RolloutPackageConfigurationDto } from "./EditRolloutPackageDesiredConfig";
import { RolloutPackageDesiredConfigFilterValues } from "./RolloutPackageDesiredConfigFilter";
import { RolloutPackageDesiredConfigTable } from "./RolloutPackageDesiredConfigTable";

export interface RolloutPackageDesiredConfigFormProps {
  isReadOnly: boolean;
  configureSoftwarePackage: RolloutPackageConfigureDto;
  filter: RolloutPackageDesiredConfigFilterValues;
  onDismiss: () => void;
  onUpdate: (values: RolloutPackageDesiredConfigFormValues[]) => void;
  rolloutPackages: RolloutPackageFormValues[];
}

export const RolloutPackageDesiredConfigForm = ({
  isReadOnly,
  configureSoftwarePackage,
  filter,
  onDismiss,
  onUpdate,
  rolloutPackages
}: RolloutPackageDesiredConfigFormProps) => {
  const theme = useTheme();
  const {
    softwarePackageId,
    softwarePackageVersionId
  } = configureSoftwarePackage;

  const {
    data: softwarePackageVersionConfigData,
    isLoading: softwarePackageVersionConfigIsLoading,
    error: softwarePackageVersionConfigError
  } = useSoftwarePackageVersionConfig(softwarePackageVersionId);

  const isLoading = softwarePackageVersionConfigIsLoading;

  const errorMessage = softwarePackageVersionConfigError?.message;

  const rolloutPackage = rolloutPackages?.find(
    rolloutPackage => rolloutPackage.softwarePackageId === softwarePackageId
  );

  const save = async (values: RolloutPackageFormValues) => {
    onUpdate(values.desiredConfigs);
    onDismiss();
  };

  const dismiss = () => {
    setExistingRolloutPackage(rolloutPackage!);
    onDismiss?.();
  };

  const [existingRolloutPackage, setExistingRolloutPackage] = useState<
    RolloutPackageFormValues
  >(rolloutPackage!);

  return (
    <SubmissionForm<RolloutPackageFormValues>
      onSubmit={save}
      cancelButtonProps={{ text: "Close", onClick: dismiss }}
      initialValues={existingRolloutPackage}
    >
      {() => (
        <Stack tokens={{ childrenGap: theme.spacing.m }}>
          <FieldArray<
            RolloutPackageDesiredConfigFormValues
          > name="desiredConfigs">
            {(_names, action, desiredConfigs) => {
              const getDesiredConfigIndexByKey = (key: string) => {
                return desiredConfigs.findIndex(x => x.key === key);
              };

              const items = softwarePackageVersionConfigData ?? [];
              const combinedItems = getCombinedItems(items, desiredConfigs);

              const filteredValues = filterAndSortDesiredConfigs(
                combinedItems,
                filter
              );

              return (
                <>
                  {softwarePackageVersionConfigData?.length === 0 ? (
                    <NoDataTile
                      styles={{ root: { height: 500 } }}
                      showBoxShadow={false}
                      textProps={{
                        text: "No configuration settings found."
                      }}
                      linkProps={{}}
                    />
                  ) : (
                    <RolloutPackageDesiredConfigTable
                      isReadOnly={isReadOnly}
                      onUpdateRolloutPackageDesiredConfig={(key, value) => {
                        const defaultValue = items.find(x => {
                          const defaultConfigItem = x as SoftwarePackageVersionConfig;
                          return defaultConfigItem.key === key;
                        }) as SoftwarePackageVersionConfig;

                        const desiredConfigIndex = getDesiredConfigIndexByKey(
                          key
                        );

                        if (desiredConfigIndex >= 0) {
                          if (defaultValue.defaultValue === value) {
                            // It's same as default - remove it
                            action.remove(desiredConfigIndex);
                          } else {
                            // Update it
                            const existingConfig =
                              desiredConfigs[desiredConfigIndex];

                            action.update(desiredConfigIndex, {
                              ...existingConfig,
                              value
                            });
                            existingConfig.value = value;
                          }
                        } else {
                          action.push({
                            id: "",
                            key,
                            value,
                            softwarePackageId: rolloutPackage?.softwarePackageId!
                          });
                        }
                      }}
                      onDeleteRolloutPackageDesiredConfig={index => {
                        const thisItem = items[
                          index
                        ] as SoftwarePackageVersionConfig;

                        const desiredConfigIndex = getDesiredConfigIndexByKey(
                          thisItem.key
                        );

                        if (desiredConfigIndex === -1) return;

                        action.remove(desiredConfigIndex);
                      }}
                      enableShimmer={isLoading}
                      errorMessage={errorMessage}
                      items={filteredValues}
                    />
                  )}
                </>
              );
            }}
          </FieldArray>
        </Stack>
      )}
    </SubmissionForm>
  );
};

const filterAndSortDesiredConfigs = (
  desiredConfigs: RolloutPackageConfigurationDto[],
  filter: RolloutPackageFilterValues
): RolloutPackageConfigurationDto[] => {
  return desiredConfigs
    .filter(
      d =>
        filter.name === "" ||
        d.key.toLowerCase().indexOf(filter.name.toLowerCase()) >= 0
    )
    .sort(
      (
        a: RolloutPackageConfigurationDto,
        b: RolloutPackageConfigurationDto
      ) => {
        return a.key < b.key ? -1 : 1;
      }
    );
};

// Combine software package version configs and rollout package desired configs into a set of dtos for this rollout package
function getCombinedItems(
  softwarePackageVersionConfigs: SoftwarePackageVersionConfig[],
  rolloutPackageDesiredConfigs: RolloutPackageDesiredConfigFormValues[]
): RolloutPackageConfigurationDto[] {
  const configurations = softwarePackageVersionConfigs.map(
    softwarePackageVersionConfig => {
      const desiredConfig = rolloutPackageDesiredConfigs?.find(
        x => x.key === softwarePackageVersionConfig.key
      );

      const {
        key,
        softwarePackageVersionId,
        defaultValue,
        dataType,
        description
      } = softwarePackageVersionConfig;

      return {
        value:
          desiredConfig?.value ?? softwarePackageVersionConfig?.defaultValue,
        key,
        softwarePackageVersionId,
        defaultValue,
        dataType,
        description
      } as RolloutPackageConfigurationDto;
    }
  );

  return configurations;
}
